import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

// -- Pasteurization Status -----------------------------------------------

// 'Failed' was removed: temperature and hold time are now selected from a
// controlled dropdown of pre-approved combinations, so pasteurization can
// no longer produce a failed pool status.
export const PasteurizationStatusSchema = z.enum(['Prep', 'Quarantined', 'Completed']);
export type PasteurizationStatus = z.infer<typeof PasteurizationStatusSchema>;

// -- Add Pasteurization Details Request ----------------------------------

/**
 * Request body for recording pasteurization details on a milk pool.
 *
 * start_time  – ISO-8601 datetime string (mandatory)
 * max_temp    – temperature reached in Celsius (mandatory)
 * hold_time   – hold time in minutes, max 500 (mandatory)
 * location_id – optional new storage location UUID
 */
export const AddPasteurizationDetailsRequestSchema = z.object({
  pool_id: z.string().min(1, 'Pool ID is required'),
  start_time: z.string().datetime({ message: 'Start time must be a valid ISO-8601 datetime' }),
  max_temp: z.number({ message: 'Temperature is required' }),
  hold_time: z
    .number({ message: 'Hold time is required' })
    .min(1, 'Hold time must be at least 1 minute')
    .max(500, 'Hold time must not exceed 500 minutes'),
  location_id: uuidSchema.optional().nullable(),
});

export type AddPasteurizationDetailsRequest = z.infer<
  typeof AddPasteurizationDetailsRequestSchema
>;

// -- Pasteurization Outcome (returned by service after evaluation) -------

export const PasteurizationOutcomeSchema = z.enum(['Passed']);
export type PasteurizationOutcome = z.infer<typeof PasteurizationOutcomeSchema>;

export const PasteurizationResultSchema = z.object({
  pool_id: z.string(),
  outcome: PasteurizationOutcomeSchema,
  new_status: PasteurizationStatusSchema,
  message: z.string(),
  expiry_date: z.string().optional().nullable(),
  location: z.string().optional().nullable(),
});

export type PasteurizationResult = z.infer<typeof PasteurizationResultSchema>;

// -- Pasteurization Device Schemas ----------------------------------------

export const PasteurizationDeviceSchema = z.object({
  pasteurization_id: uuidSchema,
  hospital_id: uuidSchema,
  pasteurization_code: z.string().max(50),
  name: z.string().max(200),
  is_active: z.boolean(),
});
export type PasteurizationDevice = z.infer<typeof PasteurizationDeviceSchema>;

export const CreatePasteurizationDeviceRequestSchema = z.object({
  hospital_id: uuidSchema,
  name: z.string().min(1, 'Name is required').max(200),
});
export type CreatePasteurizationDeviceRequest = z.infer<typeof CreatePasteurizationDeviceRequestSchema>;

export const CreatePasteurizationDeviceResponseSchema = z.object({
  pasteurization_id: uuidSchema,
  hospital_id: uuidSchema,
  pasteurization_code: z.string(),
  name: z.string(),
  is_active: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type CreatePasteurizationDeviceResponse = z.infer<typeof CreatePasteurizationDeviceResponseSchema>;

export const ActivePasteurizationDeviceSchema = z.object({
  pasteurization_id: uuidSchema,
  hospital_id: uuidSchema,
  pasteurization_code: z.string(),
  name: z.string(),
  is_active: z.literal(true),
});
export type ActivePasteurizationDevice = z.infer<typeof ActivePasteurizationDeviceSchema>;

export const UpdatePasteurizationDeviceStatusRequestSchema = z.object({
  is_active: z.boolean(),
});
export type UpdatePasteurizationDeviceStatusRequest = z.infer<typeof UpdatePasteurizationDeviceStatusRequestSchema>;

export const UpdatePasteurizationDeviceStatusResponseSchema = z.object({
  pasteurization_id: uuidSchema,
  hospital_id: uuidSchema,
  pasteurization_code: z.string(),
  name: z.string(),
  is_active: z.boolean(),
  updated_at: z.string().datetime(),
});
export type UpdatePasteurizationDeviceStatusResponse = z.infer<typeof UpdatePasteurizationDeviceStatusResponseSchema>;
